// The ranking table with its filter panel (legacy window/ranking.js), used by the Ranking window
// (global) and the Dashboard tabs (personal, global). Filters are kept in localStorage
// `solitaire_storage:public_rank_filter:<key>`; Ok saves and refetches, Cancel only hides the panel.
import { useEffect, useRef, useState } from 'react';
import { get } from '../api/client';
import { env } from '../env';
import { GAME_IDS } from '../engine/types';
import { variantOf } from '../engine/gameRegistry';
import { t } from '../i18n';
import { flagImgHtml } from '../options/assets';
import { withIndicator } from '../ui/busy';
import { formatWinTime } from './WinWindow';

export type StatsTarget = 'global' | 'personal';
export interface Filters {
  game_type: string;
  sort_type: string;
  time_range_type: string;
  won_method_type: string;
  /** The game's variant (engine/gameRegistry.ts GAME_VARIANTS), only with its game: Mahjong's shape (owner 2026-10-27,
   *  'all-layouts' or a layout name), 3-5-8's length and Tysiąc's target (owner 2026-10-28, 'all' or a number). */
  layout_type: string;
  length_type: string;
  target_type: string;
  /** Kulki's version (owner 2026-11-03, 'all', '5' or '4'). */
  line_type: string;
}
export const DEFAULT_FILTERS: Filters = {
  game_type: 'all-games',
  sort_type: 'created-desc',
  time_range_type: 'today',
  won_method_type: 'new-games',
  layout_type: 'all-layouts',
  length_type: 'all',
  target_type: 'all',
  line_type: 'all',
};
/** The request's filters: the game's variant only with its game and when one is picked (the other games' requests stay
 *  as they were). */
const query = (f: Filters): Record<string, string> => {
  const { layout_type, length_type, target_type, line_type, ...rest } = f;
  const v = variantOf(rest.game_type);
  const picked = v ? { layout_type, length_type, target_type, line_type }[v.param] : '';
  if (v && picked !== v.all) return { ...rest, [v.param]: picked };
  return rest;
};
/** The picked variant in words ("Turtle", "6 deals", "All shapes"). */
const variantName = (game: string, f: Filters): string => {
  const v = variantOf(game)!;
  const picked = f[v.param];
  return t(picked === v.all ? v.allKey : v.nameKey(picked));
};
const FILTER_KEY = (k: string) => `solitaire_storage:public_rank_filter:${k}`;

/** Public ranking: the first common mail domain in a nickname is masked (legacy common.js hideEmail). */
const hideEmail = (name: string): string =>
  name.replace(/gmail\.(c(o(m)?)?)?|yahoo\.(c(o(m)?)?)?|wp\.(p(l)?)?|hotmail\.(c(o(m)?)?)?/, '...');

export function loadFilters(): Filters {
  const f = { ...DEFAULT_FILTERS };
  for (const k of Object.keys(f) as Array<keyof Filters>) {
    try {
      const v = localStorage.getItem(FILTER_KEY(k));
      if (v !== null) f[k] = v;
    } catch {
      /* no storage */
    }
  }
  if (!sortsOf(f.game_type).includes(f.sort_type)) f.sort_type = 'score-desc'; // Kulki: points, not time
  return f;
}
export function saveFilters(f: Filters): void {
  for (const k of Object.keys(f) as Array<keyof Filters>) {
    try {
      localStorage.setItem(FILTER_KEY(k), f[k]);
    } catch {
      /* no storage */
    }
  }
}

interface Row {
  id?: string;
  player_name: string;
  game_type: string;
  scoring_type: string;
  time: string | number;
  score: string | number;
  moves: string | number;
  is_retry_deal: string | number;
  created_at: string;
  flag_src?: string;
  /** The deal's variant (server/routes/stats.js winVariants; engine/gameRegistry.ts `field`): Mahjong's layout for a win
   *  stored since 2026-10-27, 3-5-8's length and Tysiąc's target since 2026-10-28. */
  layout?: string;
  variant?: string;
}

const SORTS = ['score-desc', 'time-asc', 'moves-asc', 'created-desc'];
/** Kulki (2026-11-03): a game always ends on a full board, so a short time or few moves is a poor game; its list sorts by
 *  points or by date only (the list of every game leaves Kulki out of these sorts, server/routes/stats.js). */
const KULKI_SORTS = ['score-desc', 'created-desc'];
const sortsOf = (game: string) => (game === 'kulki' ? KULKI_SORTS : SORTS);
const RANGES = ['today', 'last-week', 'all-time'];
const METHODS = ['new-games', 'renewed-deals', 'both-methods'];
/** The filter drawer's slide-out (windows.css stats-drawer-out). */
const CLOSE_MS = 200;

/** The row's variant in words (its game's field: Mahjong's layout, the others' variant), or nothing. */
const rowVariant = (row: Row) => {
  const v = variantOf(row.game_type);
  const value = v ? row[v.field] : undefined;
  return v && value ? <span className="game_layout">{t(v.nameKey(value))}</span> : null;
};

/** Escape like legacy _getSafePlayerName (the name is user input). */
const safe = (s: string) =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);

export function StatsPanel({
  target,
  filters,
  onFilters,
  playerFlag,
}: {
  target: StatsTarget;
  filters: Filters;
  onFilters: (f: Filters) => void;
  playerFlag?: string | undefined;
}) {
  const key = `${target}|${JSON.stringify(query(filters))}`;
  const [result, setResult] = useState<{ key: string; rows: Row[] | null; failed: boolean }>({
    key: '',
    rows: null,
    failed: false,
  });
  const [panel, setPanel] = useState(false);
  // The panel slides out before it is hidden (windows.css .stats_filter[data-closing]); a timer, like the
  // windows' own close, so the tests' fake clocks drive it too.
  const [closing, setClosing] = useState(false);
  // the drawer opens at its top (it kept the scroll of the last time it was open)
  const filterRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (panel && filterRef.current) filterRef.current.scrollTop = 0;
  }, [panel]);
  const closePanel = () => {
    setClosing(true);
    window.setTimeout(() => {
      setPanel(false);
      setClosing(false);
    }, CLOSE_MS);
  };
  const [draft, setDraft] = useState<Filters>(filters);
  useEffect(() => {
    let active = true;
    void withIndicator(() => get('stats/get.json', { target, ...query(filters) })).then((r) => {
      if (!active) return;
      if (r.result === 'ok' && Array.isArray(r.response)) setResult({ key, rows: r.response as Row[], failed: false });
      else setResult({ key, rows: null, failed: true });
    });
    return () => {
      active = false;
    };
    // The filter values are part of `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const loading = result.key !== key;
  const rows = loading ? null : result.rows;
  const failed = !loading && result.failed;

  const label = (key: keyof Filters, value: string) =>
    key === 'game_type' ? t(value === 'all-games' ? 'all-games' : `win.${value}`) : t(`filter-${value}`);
  const bold = (re: RegExp, text: string | number) =>
    re.test(filters.sort_type) ? <b>{text}</b> : <span>{text}</span>;
  const radio = (id: keyof Filters, values: string[]) => (
    <div className="option_item radio">
      {values.map((v) => (
        <a
          key={v}
          href="#"
          className={`option_item_entry${draft[id] === v ? ' selected' : ''}`}
          data-item={`${id}|${v}`}
          onClick={(e) => {
            e.preventDefault();
            setDraft({ ...draft, [id]: v });
          }}
        >
          {t(`filter-${v}`)}
        </a>
      ))}
    </div>
  );

  return (
    <>
      {/* the results stay under the panel (owner 2026-10-17: the panel is "overlay nad danymi") */}
      <div className="stats_results">
        <div className="stats_table_cont">
          <table>
            <thead>
              <tr className="odd">
                <th className="thin">{t('stats-lp')}</th>
                <th>{t('winGamePlayer').replace(':', '')}</th>
                <th>{t('winGameTypeHead').replace(':', '')}</th>
                <th
                  className="thin"
                  dangerouslySetInnerHTML={{ __html: t('winGameScoringHead').replace(':', '') + ' *' }}
                />
                <th
                  className="thin"
                  dangerouslySetInnerHTML={{ __html: t('winGameMethodHead').replace(':', '') + ' *' }}
                />
                <th>{t('winTimeHead').replace(':', '')}</th>
                <th>{t('winScoresHead').replace(':', '')}</th>
                <th>{t('winMovesHead').replace(':', '')}</th>
                <th>{t('winCreatedAtHead').replace(':', '')}</th>
              </tr>
            </thead>
            <tbody>
              {rows === null ? (
                <tr>
                  <td colSpan={9}>
                    {failed ? t('stats.load-error') || 'Stats could not be loaded' : `${t('game.loading')} ...`}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9}>{t('stats.items-not-found')}</td>
                </tr>
              ) : (
                rows.map((row, i) => {
                  const flag = target === 'personal' ? playerFlag : row.flag_src;
                  const game = t(`win.${row.game_type}`) || row.game_type;
                  return (
                    <tr key={i} className={i % 2 ? '' : 'odd'}>
                      <td>{i + 1}</td>
                      <td
                        dangerouslySetInnerHTML={{
                          __html: safe(hideEmail(row.player_name)) + (flag ? ` <br />(${flagImgHtml(flag)})` : ''),
                        }}
                      />
                      <td>
                        {row.id ? (
                          <a target="_blank" rel="noreferrer" href={`${env.fullAddr}my-score/${row.id}.html`}>
                            {game}
                          </a>
                        ) : (
                          game
                        )}
                        {rowVariant(row)}
                      </td>
                      <td>{String(row.scoring_type).charAt(0).toUpperCase()}</td>
                      <td>{Number(row.is_retry_deal) ? 'R' : 'N'}</td>
                      <td>{bold(/time/, formatWinTime(Number(row.time) || 0))}</td>
                      <td>{bold(/score/, row.score)}</td>
                      <td>{bold(/moves/, row.moves)}</td>
                      <td>{bold(/created/, row.created_at)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div
          className="rows filter"
          onClick={() => {
            setDraft(filters);
            setPanel(true);
          }}
        >
          <span className="game_type">
            {t('stats.game-by')}: <strong>{label('game_type', filters.game_type)}</strong>
          </span>
          {variantOf(filters.game_type) ? (
            <span className={variantOf(filters.game_type)!.param}>
              {t(variantOf(filters.game_type)!.labelKey)}: <strong>{variantName(filters.game_type, filters)}</strong>
            </span>
          ) : null}
          <span className="sort_type">
            {t('stats.sort-by')}: <strong>{label('sort_type', filters.sort_type)}</strong>
          </span>
          <span className="time_range_type">
            {t('stats.time-by')}: <strong>{label('time_range_type', filters.time_range_type)}</strong>
          </span>
          <span className="change_filtr">
            <b>{t('stats.change-filter')}</b>
          </span>
        </div>
        <div className="rows legend">
          <span>{t('stats.legend')}</span>
        </div>
      </div>
      {/* Legacy slid the panel in from the left over a hidden table; here it is a drawer that slides in from
          the right over the dimmed results (windows.css .stats_scrim / .stats_filter); a tap on the scrim
          is Cancel. */}
      {panel ? <div className="stats_scrim" data-closing={closing || undefined} onClick={closePanel} /> : null}
      <div
        ref={filterRef}
        className="stats_filter"
        data-closing={closing || undefined}
        style={{ display: panel ? 'block' : 'none' }}
      >
        {/* the sections one under another and one action row pinned at the bottom (owner 2026-10-17: the
            panel "się krzaczy") */}
        <div className="filter_sections">
          <div className="filter_section">
            <div className="option_item label">
              <div className="header_label">{t('stats.game-by')}:</div>
            </div>
            <div className="option_item select">
              <select
                id="game_type"
                className="option_item_entry"
                value={draft.game_type}
                // Kulki: its list is a ranking by points (owner 2026-11-03), so picking it starts there
                onChange={(e) => setDraft({ ...draft, game_type: e.target.value, ...(e.target.value === 'kulki' || !sortsOf(e.target.value).includes(draft.sort_type) ? { sort_type: 'score-desc' } : {}) })}
              >
                <option value="all-games">{t('all-games')}</option>
                {GAME_IDS.map((id) => (
                  <option key={id} value={id}>
                    {t(id)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {variantOf(draft.game_type) ? (
            <div className="filter_section">
              <div className="option_item label">
                <div className="header_label">{t(variantOf(draft.game_type)!.labelKey)}:</div>
              </div>
              <div className="option_item select">
                <select
                  id={variantOf(draft.game_type)!.param}
                  className="option_item_entry"
                  value={draft[variantOf(draft.game_type)!.param]}
                  onChange={(e) => setDraft({ ...draft, [variantOf(draft.game_type)!.param]: e.target.value })}
                >
                  <option value={variantOf(draft.game_type)!.all}>{t(variantOf(draft.game_type)!.allKey)}</option>
                  {variantOf(draft.game_type)!.values.map((val) => (
                    <option key={val} value={val}>
                      {t(variantOf(draft.game_type)!.nameKey(val))}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : null}
          <div className="filter_section">
            <div className="option_item label">
              <div className="header_label">{t('stats.sort-by')}:</div>
            </div>
            {radio('sort_type', sortsOf(draft.game_type))}
          </div>
          <div className="filter_section">
            <div className="option_item label">
              <div className="header_label">{t('stats.time-by')}:</div>
            </div>
            {radio('time_range_type', RANGES)}
          </div>
          <div className="filter_section">
            <div className="option_item label">
              <div className="header_label">{t('stats.won-method')}:</div>
            </div>
            {radio('won_method_type', METHODS)}
          </div>
        </div>
        <div className="filter_actions">
          <div className="stats_cancel_filtr_button">
            <input type="button" value={t('cancel')} onClick={closePanel} />
          </div>
          <div className="stats_ok_filtr_button">
            <input
              type="button"
              value="Ok"
              onClick={() => {
                saveFilters(draft);
                onFilters(draft);
                closePanel();
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
